import { apiClient } from '@/services/api-client';
import type {
  CustomerCreditHistoryRecord,
  CustomerCreditPackageSummary,
  CustomerCreditsBalance,
  CustomerCreditsBalanceResponse,
  CustomerCreditsExpiringResponse,
  CustomerCreditsHistoryResponse,
} from '@/types/customer-credits';

type RawPackage = Record<string, unknown> & {
  bonus_credits?: number | null;
  total_credits?: number | null;
};

type RawHistoryRecord = Record<string, unknown> & {
  credit_package_id?: string;
  credits_purchased?: number;
  credits_remaining?: number;
  credits_used?: number;
  usage_percentage?: number;
  expires_at?: string | null;
  days_until_expiry?: number | null;
  is_completed?: boolean;
  is_valid?: boolean;
  has_expired?: boolean;
  paid_at?: string | null;
  formatted_amount?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

const readString = (value: unknown): string | null => (typeof value === 'string' ? value : null);
const readNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};
const readBoolean = (value: unknown): boolean | undefined => (typeof value === 'boolean' ? value : undefined);

const normalizePackage = (raw?: RawPackage | null): CustomerCreditPackageSummary | null => {
  if (!raw) return null;
  const id = readString(raw.id) ?? '';
  const name = readString(raw.name) ?? '';
  const credits = readNumber(raw.credits) ?? 0;
  return {
    id,
    name,
    credits,
    bonusCredits: readNumber(raw.bonusCredits ?? raw.bonus_credits),
    totalCredits: readNumber(raw.totalCredits ?? raw.total_credits),
  };
};

const normalizeHistoryRecord = (raw: RawHistoryRecord): CustomerCreditHistoryRecord => ({
  id: readString(raw.id) ?? '',
  creditPackageId: readString(raw.creditPackageId ?? raw.credit_package_id),
  package: normalizePackage((raw.package as RawPackage | null | undefined) ?? null),
  amount: readNumber(raw.amount),
  currency: readString(raw.currency),
  creditsPurchased: readNumber(raw.creditsPurchased ?? raw.credits_purchased),
  creditsRemaining: readNumber(raw.creditsRemaining ?? raw.credits_remaining),
  creditsUsed: readNumber(raw.creditsUsed ?? raw.credits_used),
  usagePercentage: readNumber(raw.usagePercentage ?? raw.usage_percentage),
  expiresAt: readString(raw.expiresAt ?? raw.expires_at),
  daysUntilExpiry: readNumber(raw.daysUntilExpiry ?? raw.days_until_expiry),
  status: readString(raw.status),
  isCompleted: readBoolean(raw.isCompleted ?? raw.is_completed),
  isValid: readBoolean(raw.isValid ?? raw.is_valid),
  hasExpired: readBoolean(raw.hasExpired ?? raw.has_expired),
  paidAt: readString(raw.paidAt ?? raw.paid_at),
  formattedAmount: readString(raw.formattedAmount ?? raw.formatted_amount),
  createdAt: readString(raw.createdAt ?? raw.created_at),
  updatedAt: readString(raw.updatedAt ?? raw.updated_at),
  metadata: (raw.metadata as Record<string, unknown> | undefined) ?? null,
});

class CustomerCreditsService {
  async getBalance(): Promise<CustomerCreditsBalance> {
    const response = await apiClient.get<CustomerCreditsBalanceResponse>('/api/v1/customer/credits/balance');
    const raw = response.data as unknown;
    const payload =
      raw && typeof raw === 'object' && 'data' in (raw as Record<string, unknown>)
        ? (raw as { data?: unknown }).data
        : raw;
    const record = payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};
    return {
      customerId: readString(record.customerId ?? record.customer_id) ?? '',
      availableCredits: readNumber(record.availableCredits ?? record.available_credits) ?? 0,
    };
  }

  async getHistory(): Promise<CustomerCreditHistoryRecord[]> {
    const response = await apiClient.get<CustomerCreditsHistoryResponse>('/api/v1/customer/credits/history');
    const raw = response.data as unknown;
    const payload =
      raw && typeof raw === 'object' && 'data' in (raw as Record<string, unknown>)
        ? (raw as { data?: unknown }).data
        : raw;
    const data = Array.isArray(payload) ? (payload as RawHistoryRecord[]) : [];
    return data.map((item) => normalizeHistoryRecord(item));
  }

  async getExpiring(days = 30): Promise<{ records: CustomerCreditHistoryRecord[]; days: number }> {
    const response = await apiClient.get<CustomerCreditsExpiringResponse>(`/api/v1/customer/credits/expiring?days=${days}`);
    const raw = response.data as unknown;
    const payload =
      raw && typeof raw === 'object' && 'data' in (raw as Record<string, unknown>)
        ? (raw as { data?: unknown }).data
        : raw;
    const data = Array.isArray(payload) ? (payload as RawHistoryRecord[]) : [];
    const metaDays = response.meta?.days;
    return {
      records: data.map((item) => normalizeHistoryRecord(item)),
      days: typeof metaDays === 'number' ? metaDays : days,
    };
  }
}

export const customerCreditsService = new CustomerCreditsService();
